import { usersService } from '@/services';
import { useDebouncedValue } from '@/shared/utils';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { isAuditActorId } from '../utils';

/** Poucos candidatos bastam: o administrador refina digitando mais. */
const PAGE_SIZE = 20;

/** Abaixo disso a busca aproximada do InsureMO devolve metade da base. */
const MIN_SEARCH_LENGTH = 2;

export interface AuditActorOption {
  /** `sub` do Cognito — é por ele que a trilha indexa o autor. */
  value: string;
  label: string;
  /** Id colado direto: o rótulo é o próprio id, não um nome a lembrar. */
  isRawId?: boolean;
}

/**
 * Candidatos a autor para o recorte "Por autor".
 *
 * A trilha só consulta por `sub`, então a busca por nome/e-mail passa pela
 * listagem de usuários (User_Profiles), que devolve o `identityId` de cada um.
 * Um id colado direto não consulta nada: vira a própria opção.
 */
export function useAuditActorOptions(search: string) {
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const searchesUsers =
    debouncedSearch.length >= MIN_SEARCH_LENGTH &&
    !isAuditActorId(debouncedSearch);

  const { data, isFetching } = useQuery({
    queryKey: ['audit-actor-options', debouncedSearch],
    queryFn: async () => {
      const response = await usersService.getUsers({
        search: debouncedSearch,
        limit: PAGE_SIZE,
      });

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao buscar usuários');
      }

      return response.data.data;
    },
    enabled: searchesUsers,
    staleTime: 60_000,
  });

  const options = useMemo<AuditActorOption[]>(() => {
    if (isAuditActorId(debouncedSearch)) {
      return [
        {
          value: debouncedSearch,
          label: `ID do usuário: ${debouncedSearch}`,
          isRawId: true,
        },
      ];
    }

    if (!searchesUsers) return [];

    return (data ?? [])
      .filter(user => user.identityId)
      .map(user => ({
        value: user.identityId as string,
        label: user.email ? `${user.name} · ${user.email}` : user.name,
      }));
  }, [data, debouncedSearch, searchesUsers]);

  return {
    options,
    // Enquanto o debounce não assentou, a lista ainda é a da busca anterior.
    isLoading: searchesUsers && isFetching,
    needsMoreChars:
      debouncedSearch.length < MIN_SEARCH_LENGTH && !isAuditActorId(search),
  };
}
